import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Modal, message, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import CalculateIcon from '../../../../../img/client/icons/calculate_action.svg';
import StepWizard from '../StepWizard';
import Step1Cultures from '../steps/Step1Cultures';
import Step2TreatmentType from '../steps/Step2TreatmentType';
import Step3ApplicationTechnique from '../steps/Step3ApplicationTechnique';
import Step4CommercialProducts from '../steps/Step4CommercialProducts';
import { useCalculateRisks, useValidateSelections } from '../../hooks/useRiskCalculatorData';
import { STEPS } from '../../constants';

/**
 * ScenarioModal Component
 * Modal wrapper for adding, editing, or duplicating risk calculation scenarios
 * Uses local state isolated from URL state until submission
 *
 * @param {boolean} isOpen - Whether the modal is visible
 * @param {'add' | 'edit' | 'duplicate'} mode - Modal mode
 * @param {Object} initialData - Initial data for pre-filling (for edit/duplicate)
 * @param {Object} parentEnrichedData - Enriched data from parent (for context)
 * @param {Function} onClose - Handler for modal close
 * @param {Function} onSubmit - Handler for successful submission with new ResultRow(s)
 */
const ScenarioModal = ({
  isOpen,
  mode = 'add',
  initialData = null,
  parentEnrichedData = null,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation('riskCalculator');

  // Modal local state (isolated from URL state until submission)
  const [currentStep, setCurrentStep] = useState(STEPS.CULTURES);
  const [modalSelections, setModalSelections] = useState({
    cultures: [],
    treatmentType: null,
    applicationTechnique: null,
    commercialProducts: [],
  });

  // Enriched data for display (labels, names)
  const [enrichedData, setEnrichedData] = useState({
    cultures: [],
    cultureChildren: [],
    treatmentType: null,
    applicationTechnique: null,
    products: [],
  });

  // Track step warnings (for when values may not match current filters)
  const [stepWarnings, setStepWarnings] = useState({});

  // Bulk validation hook - enabled for all modes when cultures are selected
  const {
    data: validationData,
    isFetching: isValidating,
  } = useValidateSelections(modalSelections, {
    enabled: isOpen && modalSelections.cultures.length > 0,
  });

  // Track previous validation data to avoid duplicate updates
  const prevValidationRef = useRef(null);

  // Reset warnings when starting a new validation
  useEffect(() => {
    if (isValidating) {
      setStepWarnings({});
      prevValidationRef.current = null;
    }
  }, [isValidating]);

  // Update step warnings based on bulk validation results
  useEffect(() => {
    if (!validationData || isValidating) return;

    // Create a key to check if validation changed
    const validationKey = JSON.stringify(validationData.validation);
    if (prevValidationRef.current === validationKey) return;
    prevValidationRef.current = validationKey;

    const { validation } = validationData;
    const newWarnings = {};

    // Check treatment type
    if (modalSelections.treatmentType && !validation.treatment_type.valid) {
      newWarnings[STEPS.TREATMENT_TYPE] = t('warning_treatment_type_mismatch');
    } else if (validation.treatment_type.available_count === 0 && !modalSelections.treatmentType) {
      newWarnings[STEPS.TREATMENT_TYPE] = t('warning_no_treatment_types');
    }

    // Check application technique
    if (modalSelections.applicationTechnique && !validation.application_technique.valid) {
      newWarnings[STEPS.APPLICATION_TECHNIQUE] = t('warning_technique_mismatch');
    } else if (modalSelections.treatmentType && validation.application_technique.available_count === 0 && !modalSelections.applicationTechnique) {
      newWarnings[STEPS.APPLICATION_TECHNIQUE] = t('warning_no_techniques');
    }

    // Check products
    if (validation.products.invalid_products?.length > 0) {
      newWarnings[STEPS.COMMERCIAL_PRODUCTS] = t('warning_products_mismatch');
    }

    // Check units
    if (validation.units.invalid_units?.length > 0) {
      // Combine with products warning if exists
      newWarnings[STEPS.COMMERCIAL_PRODUCTS] = newWarnings[STEPS.COMMERCIAL_PRODUCTS]
        ? t('warning_products_units_mismatch')
        : t('warning_units_mismatch');
    }

    setStepWarnings(newWarnings);
  }, [validationData, modalSelections, isValidating, t]);

  // Initialize modal with initial data when opening
  useEffect(() => {
    if (isOpen && initialData) {
      // Edit or Duplicate mode - pre-fill from initialData
      const cultures = initialData.culture ? [initialData.culture.id] : [];
      setModalSelections({
        cultures,
        treatmentType: initialData.treatmentType?.id || null,
        applicationTechnique: initialData.applicationTechnique?.id || null,
        commercialProducts: initialData.products?.map(p => ({
          id: p.id,
          dose: p.dose,
          unit: p.unit?.id,
        })) || [],
      });

      // CRITICAL: Use enriched data from initialData to prevent "loading" tags
      setEnrichedData({
        cultures: initialData.culture ? [{
          id: initialData.culture.id,
          value: initialData.culture.id,
          title: initialData.culture.name,
          label: initialData.culture.name,
        }] : [],
        cultureChildren: [],
        treatmentType: initialData.treatmentType || null,
        applicationTechnique: initialData.applicationTechnique || null,
        products: initialData.products?.map(p => ({
          id: p.id,
          name: p.name,
          approvalNumber: p.approvalNumber || '',
          dose: p.dose,
          unit: p.unit?.id,
          unitName: p.unit?.name,
          activeSubstances: p.activeSubstances,
          listedDosages: p.listedDosages,
          labelUrl: p.labelUrl,
        })) || [],
      });

      // Clear any previous warnings and validation cache
      setStepWarnings({});
      prevValidationRef.current = null;
      setCurrentStep(STEPS.CULTURES);
    } else if (isOpen && !initialData) {
      // Add mode - reset to empty state
      setModalSelections({
        cultures: [],
        treatmentType: null,
        applicationTechnique: null,
        commercialProducts: [],
      });
      setEnrichedData({
        cultures: [],
        cultureChildren: [],
        treatmentType: null,
        applicationTechnique: null,
        products: [],
      });
      setStepWarnings({});
      prevValidationRef.current = null;
      setCurrentStep(STEPS.CULTURES);
    }
  }, [isOpen, initialData]);

  // Mismatch handlers - no-op since bulk validation handles all warnings
  // These are passed to child components but do nothing (bulk validation is authoritative)
  const handleTreatmentTypeMismatch = useCallback(() => {}, []);
  const handleApplicationTechniqueMismatch = useCallback(() => {}, []);
  const handleProductsMismatch = useCallback(() => {}, []);

  // Calculate risks mutation
  const {
    mutate: calculateRisks,
    isPending: isCalculating,
  } = useCalculateRisks({
    onSuccess: (data) => {
      // Transform API response and pass to parent
      onSubmit(data, modalSelections, enrichedData, mode);
      handleClose();
      message.success(t('calculation_complete'));
    },
    onError: (error) => {
      console.error('Risk calculation error:', error);
      message.error(
        error.response?.data?.errors?.[0] ||
          error.message ||
          t('calculation_error')
      );
    },
  });

  // Handle modal close
  const handleClose = useCallback(() => {
    setCurrentStep(STEPS.CULTURES);
    onClose();
  }, [onClose]);

  // Step navigation
  const goToStep = useCallback((step) => {
    setCurrentStep(step);
  }, []);

  const goToNextStep = useCallback(() => {
    if (currentStep < STEPS.COMMERCIAL_PRODUCTS) {
      setCurrentStep(currentStep + 1);
    }
  }, [currentStep]);

  const goToPreviousStep = useCallback(() => {
    if (currentStep > STEPS.CULTURES) {
      setCurrentStep(currentStep - 1);
    }
  }, [currentStep]);

  // Data setters - update both selections and enriched data
  const setCultures = useCallback((cultures, enrichedNodes = []) => {
    setModalSelections(prev => ({ ...prev, cultures }));
    if (enrichedNodes && enrichedNodes.length > 0) {
      setEnrichedData(prev => ({ ...prev, cultures: enrichedNodes }));
    }
  }, []);

  const setTreatmentType = useCallback((treatmentType, option = null) => {
    setModalSelections(prev => ({ ...prev, treatmentType }));
    if (option) {
      // Transform option to enriched format expected by SelectedValuesTags
      setEnrichedData(prev => ({
        ...prev,
        treatmentType: {
          id: treatmentType,
          name: option.label || option.rawData?.name || option.name,
        },
      }));
    }
  }, []);

  const setApplicationTechnique = useCallback((applicationTechnique, option = null) => {
    setModalSelections(prev => ({ ...prev, applicationTechnique }));
    if (option) {
      // Transform option to enriched format expected by SelectedValuesTags
      setEnrichedData(prev => ({
        ...prev,
        applicationTechnique: {
          id: applicationTechnique,
          name: option.label || option.rawData?.name || option.name,
        },
      }));
    }
  }, []);

  const setCommercialProducts = useCallback((commercialProducts, enriched = []) => {
    setModalSelections(prev => ({ ...prev, commercialProducts }));
    if (enriched && enriched.length > 0) {
      setEnrichedData(prev => ({ ...prev, products: enriched }));
    }
  }, []);

  // Validation
  const isStepValid = useCallback((step) => {
    switch (step) {
      case STEPS.CULTURES:
        return modalSelections.cultures.length > 0;
      case STEPS.TREATMENT_TYPE:
        return modalSelections.treatmentType !== null;
      case STEPS.APPLICATION_TECHNIQUE:
        return modalSelections.applicationTechnique !== null;
      case STEPS.COMMERCIAL_PRODUCTS:
        return modalSelections.commercialProducts.length > 0 &&
               modalSelections.commercialProducts.every(p => p.dose && p.unit);
      default:
        return false;
    }
  }, [modalSelections]);

  const canProceedToNextStep = isStepValid(currentStep);
  // canCalculate requires ALL steps to be valid, not just the last one
  const canCalculate = isStepValid(STEPS.CULTURES) &&
                       isStepValid(STEPS.TREATMENT_TYPE) &&
                       isStepValid(STEPS.APPLICATION_TECHNIQUE) &&
                       isStepValid(STEPS.COMMERCIAL_PRODUCTS);
  const isFirstStep = currentStep === STEPS.CULTURES;
  const isLastStep = currentStep === STEPS.COMMERCIAL_PRODUCTS;

  // Handle calculate
  const handleCalculate = useCallback(() => {
    if (!canCalculate) return;

    const payload = {
      cultures: modalSelections.cultures,
      treatmentType: modalSelections.treatmentType,
      applicationTechnique: modalSelections.applicationTechnique,
      commercialProducts: modalSelections.commercialProducts,
    };

    calculateRisks(payload);
  }, [canCalculate, modalSelections, calculateRisks]);

  // Render step content
  const renderStepContent = () => {
    const isSingleSelect = mode === 'edit'; // Edit mode = single culture

    switch (currentStep) {
      case STEPS.CULTURES:
        return (
          <Step1Cultures
            selectedCultures={modalSelections.cultures}
            onSelectCultures={setCultures}
            enrichedCultures={[
              ...(enrichedData.cultures || []),
              ...(enrichedData.cultureChildren || []),
            ]}
            singleSelect={isSingleSelect}
          />
        );

      case STEPS.TREATMENT_TYPE:
        return (
          <Step2TreatmentType
            selectedTreatmentType={modalSelections.treatmentType}
            onSelectTreatmentType={setTreatmentType}
            selections={modalSelections}
            enrichedData={enrichedData}
            onMismatchChange={handleTreatmentTypeMismatch}
          />
        );

      case STEPS.APPLICATION_TECHNIQUE:
        return (
          <Step3ApplicationTechnique
            selectedApplicationTechnique={modalSelections.applicationTechnique}
            onSelectApplicationTechnique={setApplicationTechnique}
            selections={modalSelections}
            enrichedData={enrichedData}
            onMismatchChange={handleApplicationTechniqueMismatch}
          />
        );

      case STEPS.COMMERCIAL_PRODUCTS:
        return (
          <Step4CommercialProducts
            selectedProducts={modalSelections.commercialProducts}
            onSelectProducts={setCommercialProducts}
            selections={modalSelections}
            enrichedData={enrichedData}
            onMismatchChange={handleProductsMismatch}
          />
        );

      default:
        return null;
    }
  };

  // Modal title based on mode
  const getModalTitle = () => {
    switch (mode) {
      case 'edit':
        return t('modal_title_edit');
      case 'duplicate':
        return t('modal_title_duplicate');
      case 'add':
      default:
        return t('modal_title_add');
    }
  };

  return (
    <Modal
      title={<span className="text-base sm:text-lg lg:text-xl">{getModalTitle()}</span>}
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      width="95vw"
      className="scenario-modal"
      style={{ maxWidth: 1400, top: 10 }}
      styles={{
        body: {
          maxHeight: 'calc(100vh - 120px)',
          overflowY: 'auto',
          padding: '12px',
        },
      }}
      destroyOnClose
    >
      <div className="px-0 sm:px-2 lg:px-4">
        {/* Step Wizard */}
        <StepWizard
          currentStep={currentStep}
          onStepClick={goToStep}
          isStepValid={isStepValid}
          selections={modalSelections}
          enrichedData={enrichedData}
          stepWarnings={stepWarnings}
        />

        {/* Step Content */}
        <div className="mt-4 sm:mt-6">
          {renderStepContent()}
        </div>

        {/* Navigation Buttons - responsive like main calculator */}
        <div className="flex flex-col sm:flex-row gap-3 sm:gap-2 justify-center items-stretch sm:items-center mt-6 pb-2">
          {!isFirstStep && (
            <Button
              type="default"
              onClick={goToPreviousStep}
              className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
              icon={<ArrowLeftOutlined />}
              disabled={isCalculating}
            >
              {t('button_previous')}
            </Button>
          )}

          {/* Next button - show when not on last step */}
          {!isLastStep && (
            <Button
              type="primary"
              onClick={goToNextStep}
              disabled={!canProceedToNextStep || isCalculating}
              className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              style={{
                cursor: canProceedToNextStep && !isCalculating
                  ? 'pointer'
                  : 'not-allowed',
              }}
            >
              {t('button_next')}
            </Button>
          )}

          {/* Calculate button - show on last step OR on any step in edit/duplicate mode when valid */}
          {(isLastStep || ((mode === 'edit' || mode === 'duplicate') && canCalculate)) && (
            <Button
              type="primary"
              onClick={handleCalculate}
              disabled={!canCalculate || isCalculating}
              className="w-full sm:w-auto h-11 sm:h-[50px] px-4 sm:px-6 text-sm sm:text-base"
              icon={<img src={CalculateIcon} alt="" className="w-5 h-5" />}
              style={{
                cursor: canCalculate && !isCalculating
                  ? 'pointer'
                  : 'not-allowed',
              }}
            >
              {isCalculating ? t('calculating') : t('button_calculate')}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ScenarioModal;